import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Loader2, X } from 'lucide-react';

export const WaitlistForm = ({ inline = false }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, name, company, description }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit');
      }
      
      const result = await response.json();
      
      toast({
        title: "Success!",
        description: "Successfully joined waitlist!",
      });
      
      setEmail('');
      setName('');
      setCompany('');
      setDescription('');
      setSuccess(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {success && (
        <div className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-lg px-4 py-3 text-center mx-auto max-w-sm mb-6 relative">
          <button
            onClick={() => setSuccess(false)}
            className="absolute top-2 right-2 text-cyan-400 hover:text-cyan-300 transition-colors"
            aria-label="Dismiss success message"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="font-medium">✓ Thank you for your interest!</div>
          <div className="text-cyan-300 text-sm mt-1">We'll be in touch soon.</div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
      <div>
        <Input
          type="text"
          placeholder="Full Name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
          required
        />
      </div>
      <div>
        <Input
          type="email"
          placeholder="Work Email *"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
          required
        />
      </div>
      <div>
        <Input
          type="text"
          placeholder="Company Name"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
        />
      </div>
      <div>
        <Textarea
          placeholder="Tell us about your use case (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="bg-white/5 border-white/10 text-white placeholder:text-gray-400 resize-none"
        />
      </div>
      <Button 
        type="submit" 
        size="lg" 
        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Joining Waitlist...
          </>
        ) : (
          'Get Early Access'
        )}
      </Button>
    </form>
    </>
  );
};
